import { useState, useMemo, useEffect } from "react";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import {
  Visibility,
  Edit,
  Delete,
  Add,
  ShieldRounded,
  WarningAmber,
  PublicRounded,
  VpnKeyRounded
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Modal,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Paper,
  Avatar,
  Stack,
  CircularProgress
} from "@mui/material";
import IdentityProvidersUpdate from "./update-identity-provider";
import IdentityProvidersDetails from "./identity-providers-details";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import moment from "moment";
import Error from "../error";
import useUserPermissions from "../../hooks/useUserPermissions";
import {
  mrtManagementTableCustomActionsOuterSx,
  mrtManagementTablePrimaryActionButtonSx,
  mrtManagementTableToolbarOptions,
} from "../../utils/mrtManagementTableToolbar";

// Identity Providers Management component powered by Material React Table
function IdentityProviders() {
  const {
    createIdentityProvider,
    getAllIdentityProviders,
    getIdentityProvider,
    updateIdentityProvider,
    deleteIdentityProvider,
  } = useUserPermissions();

  // MRT State Management
  const [globalFilter, setGlobalFilter] = useState("");
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: "name", desc: false }]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 5 });

  // Dialog & View State
  const [open, setOpen] = useState(false);
  const [view, setView] = useState(false);
  const [update, setUpdate] = useState(false);
  const [selectedProviderId, setSelectedProviderId] = useState(null);
  const [selectedProviderData, setSelectedProviderData] = useState({});

  // Safety Confirmation States
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, provider: null });
  const [editConfirm, setEditConfirm] = useState({ open: false, provider: null });

  const axiosPrivate = useAxiosPrivate();
  const { enqueueSnackbar } = useSnackbar();

  // Data Fetching Logic (Server-Side)
  const {
    isLoading: providersDataLoading,
    data: providersData,
    refetch,
    error: fetchIPError,
  } = useQuery(
    ["identity-providers", pagination, globalFilter, columnFilters, sorting],
    () => {
      const filterParams = globalFilter
        ? JSON.stringify([{
          column_field: "*",
          operator_value: "contains",
          value: JSON.stringify({
            columns: ["name", "client_id", "authorization_uri"],
            value: globalFilter
          })
        }])
        : columnFilters.length > 0
          ? JSON.stringify(columnFilters.map(f => ({
            column_field: f.id,
            operator_value: "contains",
            value: f.value
          })))
          : "[]";

      const sortParams = JSON.stringify(sorting.map(s => ({
        field: s.id,
        sort: s.desc ? "desc" : "asc"
      })));

      return axiosPrivate.get(`/identityProviders`, {
        params: {
          page: pagination.pageIndex,
          per_page: pagination.pageSize,
          sort: sortParams,
          filter: filterParams,
        },
      });
    },
    { refetchOnWindowFocus: false, enabled: !!getAllIdentityProviders }
  );

  // Mutations
  const { mutate: deleteProviderMutate, isLoading: isDeleting } = useMutation((id) =>
    axiosPrivate.delete(`/identityProviders/${id}`),
    {
      onSuccess: () => {
        enqueueSnackbar("Identity Provider deleted successfully", { variant: "success" });
        setDeleteConfirm({ open: false, provider: null });
        refetch();
      }
    }
  );

  // Handlers
  const handleView = (id) => {
    setSelectedProviderId(id);
    setView(true);
    setOpen(true);
  };

  const handleEditRequest = (provider) => {
    setSelectedProviderData(provider);
    setEditConfirm({ open: true, provider });
  };

  const handleProceedEdit = () => {
    setSelectedProviderId(selectedProviderData.id);
    setUpdate(true);
    setView(false);
    setOpen(true);
    setEditConfirm({ open: false, provider: null });
  };

  const handleOpenCreate = () => {
    setUpdate(false);
    setView(false);
    setOpen(true);
  };

  const handleModalClose = () => {
    setOpen(false);
    setUpdate(false);
    setView(false);
  };

  // Columns Definition
  const columns = useMemo(() => [
    {
      accessorKey: "name",
      header: "Identity Provider",
      size: 250,
      Cell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.lighter', color: 'primary.main', borderRadius: 2 }}>
            <VpnKeyRounded fontSize="small" />
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: 'secondary.main' }}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PublicRounded sx={{ fontSize: 12 }} /> AUTH: {row.original.authorization_uri ? new URL(row.original.authorization_uri).hostname : 'N/A'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      accessorKey: "client_id",
      header: "Client ID",
      size: 200,
      Cell: ({ cell }) => (
        <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 600, bgcolor: 'rgba(0,0,0,0.03)', p: 0.5, borderRadius: 1 }}>
          {cell.getValue()?.substring(0, 16)}...
        </Typography>
      ),
    },
    {
      accessorKey: "created_at",
      header: "Created",
      size: 150,
      Cell: ({ cell }) => moment(cell.getValue()).format("MMM D, YYYY"),
    },
  ], []);

  const table = useMaterialReactTable({
    columns,
    data: providersData?.data?.data || [],
    initialState: { showColumnFilters: false, density: 'comfortable' },
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
    ...mrtManagementTableToolbarOptions,
    muiToolbarAlertBannerProps: fetchIPError ? { color: 'error', children: 'Error loading data' } : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: providersData?.data?.meta_data?.total || 0,
    state: { columnFilters, globalFilter, isLoading: providersDataLoading, pagination, sorting },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <Tooltip title="Details">
          <IconButton size="small" onClick={() => handleView(row.original.id)} sx={{ opacity: 0.7 }}>
            <Visibility fontSize="small" />
          </IconButton>
        </Tooltip>
        {updateIdentityProvider && (
          <Tooltip title="Edit">
            <IconButton size="small" onClick={() => handleEditRequest(row.original)} color="primary">
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {deleteIdentityProvider && (
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => setDeleteConfirm({ open: true, provider: row.original })} color="error">
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    ),
    renderTopToolbarCustomActions: () => (
      <Box sx={mrtManagementTableCustomActionsOuterSx}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'primary.lighter', display: 'flex', flexShrink: 0 }}>
            <ShieldRounded sx={{ color: 'primary.main' }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'secondary.main', lineHeight: 1.2 }}>
              Identity Providers
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
              {providersData?.data?.meta_data?.total || 0} active authentication methods
            </Typography>
          </Box>
        </Stack>
        {createIdentityProvider && (
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={mrtManagementTablePrimaryActionButtonSx}
          >
            Add Provider
          </Button>
        )}
      </Box>
    ),
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 5,
        border: "1px solid", borderColor: "divider",
        boxShadow: '0 10px 40px rgba(0,0,0,0.02)',
      },
    },
    muiTableHeadCellProps: {
      sx: {
        bgcolor: 'background.default',
        fontWeight: 800,
        color: 'text.secondary',
        fontSize: '12px',
        py: 2,
      },
    },
  });

  return fetchIPError ? (
    <Error error={fetchIPError?.response?.data?.error} />
  ) : (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: 'background.default', minHeight: '100%' }}>
      <MaterialReactTable table={table} />

      {/* Modals for Detail / Update */}
      <Modal open={open} onClose={handleModalClose}>
        <Box>
          {view ? (
            <IdentityProvidersDetails handleViewModalClose={handleModalClose} identityProviderId={selectedProviderId} />
          ) : (
            <IdentityProvidersUpdate handleModalClose={handleModalClose} isNew={!update} identityProviderData={selectedProviderData} refetch={refetch} />
          )}
        </Box>
      </Modal>

      {/* Delete Confirmation */}
      <SafetyDialog
        open={deleteConfirm.open}
        title="Delete Identity Provider?"
        content={`This will permanently remove the authentication method "${deleteConfirm.provider?.name}". Existing users using this provider may lose access. Proceed?`}
        onConfirm={() => deleteProviderMutate(deleteConfirm.provider?.id)}
        onCancel={() => setDeleteConfirm({ open: false, provider: null })}
        isLoading={isDeleting}
        color="error"
      />

      {/* Edit Confirmation */}
      <SafetyDialog
        open={editConfirm.open}
        title="Modify Provider?"
        content={`Adjusting "${editConfirm.provider?.name}" configuration may impact the authentication flow. Are you sure you want to proceed?`}
        onConfirm={handleProceedEdit}
        onCancel={() => setEditConfirm({ open: false, provider: null })}
        color="warning"
      />
    </Box>
  );
}

function SafetyDialog({ open, title, content, onConfirm, onCancel, isLoading, color = "primary" }) {
  return (
    <Dialog
      open={open}
      onClose={() => !isLoading && onCancel()}
      PaperProps={{ sx: { borderRadius: 6, p: 1, maxWidth: '400px' } }}
    >
      <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
        <Box sx={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          bgcolor: `${color}.lighter`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mx: 'auto',
          mb: 2
        }}>
          <WarningAmber color={color} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{title}</Typography>
      </DialogTitle>
      <DialogContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>{content}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 1, gap: 1 }}>
        <Button fullWidth variant="outlined" color="inherit" onClick={onCancel} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button fullWidth variant="contained" color={color} onClick={onConfirm} disabled={isLoading} sx={{ borderRadius: 3, textTransform: 'none', fontWeight: 700 }}>
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "Confirm"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default IdentityProviders;
